import type { ScopeQuery } from "@app/shared"
import { Button } from "@app/ui"
import { useMutation } from "@tanstack/react-query"

import { downloadTextFile, scopedExportFilename } from "../lib/download"

/**
 * Export-to-CSV button, shared by the shipments / lots / parties lists.
 *
 * Extracted so all three behave identically — disabled state, pending label,
 * and filename convention. Three hand-rolled copies is three chances for one
 * of them to quietly export something other than what the screen is showing.
 *
 * The caller supplies the fetch, so this stays unaware of which endpoint it is
 * hitting; it owns only the browser-specific part.
 */
export interface ExportCsvButtonProps {
	/** Fetches the CSV text. Given the caller's current filters. */
	readonly fetchCsv: () => Promise<string>
	/** Filename stem: `shipments`, `lots`, `parties`. */
	readonly base: string
	/** Active scope — stamped into the filename so exports do not collide. */
	readonly scope: ScopeQuery
	/** Nothing to export. */
	readonly disabled?: boolean
}

export default function ExportCsvButton({
	fetchCsv,
	base,
	scope,
	disabled = false,
}: ExportCsvButtonProps) {
	const mutation = useMutation({
		mutationFn: async () => {
			const csv = await fetchCsv()
			downloadTextFile(csv, scopedExportFilename(base, scope, new Date()))
		},
	})

	return (
		<Button
			variant="secondary"
			size="sm"
			disabled={disabled || mutation.isPending}
			onClick={() => mutation.mutate()}
		>
			{mutation.isPending ? "Exporting…" : "Export CSV"}
		</Button>
	)
}
